import { type ReactNode, useState } from "react";

import { CheckBoldIcon } from "#icons";
import { customTv } from "#utils";

import type { VariantProps } from "tailwind-variants";

const stepperStyles = customTv({
  base: "flex items-center",
  variants: {
    spacing: {
      sm: "gap-4",
      md: "gap-6",
      lg: "gap-8",
    },
  },
  defaultVariants: {
    spacing: "md",
  },
});

const stepStyles = customTv({
  base: "flex items-center gap-2 cursor-pointer bg-transparent border-none outline-none transition-opacity duration-200 hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50",
  variants: {},
});

const stepIconStyles = customTv({
  base: "w-[24px] h-[24px] min-w-[24px] shrink-0 min-h-[24px] rounded-lg flex items-center justify-center font-family-text text-label-medium font-semibold leading-5 transition-all duration-200 border-2 capitalize",
  variants: {
    status: {
      pending: "bg-background-secondary border-background-secondary",
      completed: "bg-positive-700 border-positive-700",
      current: "bg-brand-primary border-brand-primary ",
    },
    disabled: {
      true: "opacity-50",
    },
  },
});

const stepLabelStyles = customTv({
  base: "text-sm font-semibold transition-colors duration-200",
  variants: {
    status: {
      pending: "text-text-primary",
      current: "text-text-brand",
      completed: "text-text-secondary",
    },
  },
});

export type StepStatus = "pending" | "current" | "completed";

export interface Step {
  id: string;
  label: ReactNode;
  status?: StepStatus;
  disabled?: boolean;
}

export interface StepperProps extends VariantProps<typeof stepperStyles> {
  /** Array of steps to display */
  steps: Step[];
  /** Current active step index (controlled mode) */
  currentStep?: number;
  /** Callback when a step is clicked */
  onStepClick?: (stepIndex: number, step: Step) => void;
  /** Additional CSS class name */
  className?: string;
  /** If true, allows clicking on any step regardless of completion status */
  allowSkipping?: boolean;
}

function StepIcon({ stepNumber, status }: { stepNumber: number; status: StepStatus }) {
  if (status === "completed") {
    return <CheckBoldIcon className="p-0.5 text-text-primary-inverse" />;
  }

  const textColor = status === "pending" ? "text-gray-600" : "text-text-primary-inverse";

  return <span className={textColor}>{stepNumber + 1}</span>;
}

export function Stepper({
  steps,
  currentStep,
  onStepClick,
  spacing = "md",
  allowSkipping = false,
  className,
}: StepperProps) {
  const [internalCurrentStep, setInternalCurrentStep] = useState(0);

  // Determine if component is controlled (currentStep prop is provided)
  const isControlled = currentStep !== undefined;
  const activeStep = isControlled ? currentStep : internalCurrentStep;

  const processedSteps = steps.map((step, index) => {
    let status: StepStatus = "pending";

    if (index < activeStep) {
      status = "completed";
    } else if (index === activeStep) {
      status = "current";
    }

    return {
      ...step,
      status: step.status ?? status,
    };
  });

  const handleStepClick = (stepIndex: number, step: Step) => {
    if (step.disabled) return;

    // Only allow clicking on completed steps, current step, or the next step
    // unless allowSkipping is true
    const canClick = allowSkipping || stepIndex <= activeStep || stepIndex === activeStep + 1;

    if (!canClick) return;

    // Always call onStepClick if provided
    if (onStepClick) {
      onStepClick(stepIndex, step);
    }

    // Update internal state only if component is uncontrolled
    if (!isControlled) {
      setInternalCurrentStep(stepIndex);
    }
  };

  return (
    <div className={stepperStyles({ class: className, spacing })}>
      {processedSteps.map((step, index) => (
        <div className="flex items-center" key={step.id}>
          <button
            aria-label={`Step ${index + 1}: ${step.label}`}
            className={stepStyles()}
            disabled={step.disabled}
            onClick={() => handleStepClick(index, step)}
            type="button"
          >
            <div
              className={stepIconStyles({
                disabled: step.disabled,
                status: step.status,
              })}
            >
              <StepIcon status={step.status} stepNumber={index} />
            </div>

            <div
              className={stepLabelStyles({
                status: step.status,
              })}
            >
              {step.label}
            </div>
          </button>
        </div>
      ))}
    </div>
  );
}
